import { useState, useEffect, useMemo, useRef, useCallback, StrictMode } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material'
import { Context } from './Context'
import { brandForeground, brandTintPercent } from './shared'
import type { Context as ContextType, Services } from '../design/app'
import type { Database, IAM as IAMInterface } from '../design/services'
import type { User, Project, Authorization, Model, Property, Group, Capability, Action, Config } from '../design/types'
import { LLM } from '../services/llm/client'
import { Database as LocalDatabase } from '../services/database/local'
import { IAM as LocalIAM } from '../services/iam/local'
import { IAM as FirebaseIAM } from '../services/iam/firebase'
import { Reactor as LocalReactor } from '../services/reactor/local'
import { Runner as LocalRunner } from '../services/runner/local'
import { Storage as LocalStorage } from '../services/storage/local'
import { Config as LocalConfig } from '../services/config/local'
import { Database as FirebaseDatabase } from '../services/database/firebase'
import { Config as FirebaseConfig } from '../services/config/firebase'
import { Storage as FirebaseStorage } from '../services/storage/firebase'
import { Registry as LocalRegistry } from '../services/registry/local'
import { config as mksysConfig } from '../firebase.config'
import { bootstrap } from './bootstrap'
import { Layout } from './Layout'
import { Notice } from '../components/native/Notice'
import { Login } from './Login'
import Page from './Page'
import './App.css'

/** JSON.stringify that replaces circular references with '[Circular]' */
function stringify(value: unknown): string {
  const seen = new WeakSet();
  return JSON.stringify(value, (_key, val) => {
    if (val !== null && typeof val === 'object') {
      if (seen.has(val)) return '[Circular]';
      seen.add(val);
    }
    return val;
  });
}

function resolveProjectHierarchy(
  allProjects: Project[],
  hostname: string,
): { match: Project; selectableProjects: Project[]; parent: Project | undefined } | null {
  const match = allProjects.find(p => p.url === hostname);
  if (!match) return null;
  const children = allProjects.filter(p => p.parent === match.id);
  const selectableProjects = children.length > 0 ? children : [match];
  // Children-case: all share `match` as parent. Leaf-case: resolve match's own parent (self-ref mksys → match).
  const parent = children.length > 0
    ? match
    : match.parent ? allProjects.find(p => p.id === match.parent) : undefined;
  return { match, selectableProjects, parent };
}

const model = {
  id: '?',
  name: '?',
  type: 'collection' as const,
  path: '?',
  properties: [],
  actions: []
};

// Hydration mutates storage-format records (references-as-ids) into runtime
// objects (references-as-objects). The declared Property/Model/Action types
// describe the post-hydration shape, so we reach for unresolved-shape casts.
type Hydratable = Record<string, unknown>;

function hydrateModels(models: Model[], properties: Property[], actions: Action[]): Model[] {
  if (models.length === 0 || properties.length === 0) return [];

  const modelsMap = new Map(models.map(p => [p.id, p]));
  const propsMap = new Map(properties.map(p => [p.id, p]));
  const actionsMap = new Map(actions.map(a => [a.id, a]));
  properties.forEach(prop => {
    const p = prop as Hydratable;
    if (Array.isArray(p.properties) && typeof p.properties[0] === 'string') {
      p.properties = (p.properties as string[]).map(id => propsMap.get(id));
    }
    if (p.reference && typeof p.reference === 'string') {
      p.reference = modelsMap.get(p.reference);
    }
  });
  models.forEach(model => {
    const m = model as Hydratable;
    if (Array.isArray(m.properties) && typeof m.properties[0] === 'string') {
      m.properties = (m.properties as string[]).map(id => propsMap.get(id));
    }
    if (Array.isArray(m.actions) && m.actions.length > 0 && typeof m.actions[0] === 'string') {
      m.actions = (m.actions as string[]).map(id => actionsMap.get(id)).filter(Boolean);
    }
    if (m.reference && typeof m.reference === 'string') {
      m.reference = modelsMap.get(m.reference);
    }
  });
  return [...models];
}

function App() {
  const [bootstrapped, setBootstrapped] = useState(false);
  // Don't show Login until Firebase Auth has determined whether the user is signed in or not
  const [authChecked, setAuthChecked] = useState(false);
  const [authn, setAuthn] = useState<Authorization[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [hydratedModels, setHydratedModels] = useState<Model[]>([]);
  const [selectableProjects, setSelectableProjects] = useState<Project[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [parentMetaDb, setParentMetaDb] = useState<Database | null>(null);
  // Ref mirror of parentMetaDb for closures that need the current value
  // (not the one captured at construction time). FirebaseIAM's
  // onFirebaseUser callback is constructed once and can't be rebuilt
  // when parentMetaDb changes — without this ref it would read stale.
  const parentMetaDbRef = useRef<Database | null>(null);
  useEffect(() => { parentMetaDbRef.current = parentMetaDb; }, [parentMetaDb]);
  // Tracks whether the auth chain (groups → authn) has resolved at least once.
  // Prevents flashing 404 while async discovery and auth loading are still in progress.
  const [authResolved, setAuthResolved] = useState(false);
  // True once the discover effect has finished walking the project tree,
  // regardless of whether the current hostname matched a Project. Lets us
  // tell "still loading" apart from "tree loaded, hostname not registered".
  const [discovered, setDiscovered] = useState(false);
  // IAM is constructed lazily: in prod it depends on the URL-matched project's
  // Firebase config, which only resolves after discover() completes. Holding
  // iam as state (not just a ref) drives re-renders so the render gate below
  // can hold a spinner until it arrives, and the context-sync effect can
  // swap the placeholder metaIAM out once it does.
  const [iam, setIam] = useState<LocalIAM | FirebaseIAM | null>(null);
  const [match, setMatch] = useState<Project | null>(null);
  const contextRef = useRef<ContextType>(null!);
  const getContext = () => contextRef.current;
  const pageRef = useRef({ reactiveEntities: [], model: null, navigate: () => {}, path: [] } as ContextType['page']['current']);
  const prevConditionsRef = useRef('');
  // iamRef shadows `iam` for callbacks that were baked in once via useMemo([])
  // (llm's getIdToken) and therefore can't close over state without going stale.
  const iamRef = useRef<LocalIAM | FirebaseIAM | null>(null);
  // Captured from useMemo so the prod-IAM effect's onFirebaseUser can await
  // bootstrap before doing user lookup, without re-wiring bootstrap itself.
  const bootstrapDoneRef = useRef<Promise<void> | null>(null);

  // Initialize services (memoized to prevent recreation on each render)
  const { config, devMode, getDatabase, rootMetaDb, devIam, runner, reactor, llm, registry, metaIAM, metaRunner, metaReactor } = useMemo(() => {
    const config = new LocalConfig();
    const storage = new LocalStorage(config);
    const databaseCache = new Map<string, Database>();

    const devMode = import.meta.env.VITE_DATABASE === 'local';
    const fc = new FirebaseConfig(mksysConfig.projectId, mksysConfig.appId, mksysConfig.storageBucket, mksysConfig.apiKey, mksysConfig.authDomain, mksysConfig.messagingSenderId, mksysConfig.measurementId);
    const firebaseStorage = new FirebaseStorage(fc);
    const rootMetaDb: Database = devMode
      ? new LocalDatabase(storage, 'mksys', config)
      : new FirebaseDatabase(firebaseStorage, 'mksys', fc);

    // Factory: creates or retrieves a project database based on type
    const getDatabase = (projectId: string, _type = 'local', _config?: Config): Database => {
      if (projectId === 'mksys') return rootMetaDb;
      let db = databaseCache.get(projectId);
      if (!db) {
        switch (devMode ? 'local' : _type) {
          case 'local':
            db = new LocalDatabase(storage, projectId, config);
            break;
          case 'firebase': {
            if (!_config) throw new Error(`Firebase config required for project '${projectId}'`);
            const pfc = new FirebaseConfig(_config.projectId!, _config.appId!, _config.storageBucket!, _config.apiKey!, _config.authDomain!, _config.messagingSenderId!, _config.measurementId!);
            const pfs = new FirebaseStorage(pfc);
            db = new FirebaseDatabase(pfs, projectId, pfc);
            break;
          }
          default:
            throw new Error(`Unsupported database type: ${_type}`);
        }
        databaseCache.set(projectId, db);
      }
      return db;
    };

    // Dev IAM is needed now because bootstrap's auto-login path calls iam.login().
    // Prod IAM is constructed later in a post-match effect, once we know which
    // tenant's Firebase config to use; bootstrap in prod runs with skipLogin=true
    // and never reads iam.
    const onLogin = (user: User) => setUser(user);
    const onLogout = () => setUser(null);
    const devIam = devMode ? new LocalIAM(config, onLogin, onLogout) : null;

    const runner = new LocalRunner(config);
    const reactor = new LocalReactor(runner);
    // Reads iamRef rather than a captured variable: prod IAM doesn't exist when
    // this closure is baked in, and we want getIdToken to pick up whatever
    // instance is current without re-memoizing llm.
    const getIdToken = () => {
      const current = iamRef.current;
      if (current instanceof FirebaseIAM) {
        // currentUser is null between logout and the next onAuthStateChanged
        // tick; a chat() fired in that window would have thrown on `!`.
        const fbUser = current.getAuth().currentUser;
        if (!fbUser) return Promise.reject(new Error('Not authenticated'));
        return fbUser.getIdToken();
      }
      return Promise.resolve('dev-token');
    };
    // Route chat() to the selected project's run() function so each child
    // project uses its own API key. No fallback to mksys: if the project or
    // its config hasn't populated yet (authn subscription still in flight),
    // cross-origin sends to mksys's run() fail CORS because the child's
    // hosting domain isn't in mksys's ALLOWED_ORIGINS. Fail loud instead.
    const getRunUrl = () => {
      const projectConfig = (getContext()?.project as Project | null)?.config as { projectId?: string } | undefined;
      if (!projectConfig?.projectId) throw new Error('Project config not ready');
      return `https://us-central1-${projectConfig.projectId}.cloudfunctions.net/run`;
    };
    const llm = new LLM(getRunUrl, config, getContext, getIdToken);
    const registry = new LocalRegistry(config);
    const metaIAM = new LocalIAM(config,
      (user: User) => console.log('Meta user logged in:', user),
      () => console.log('Meta user logged out')
    );
    const metaRunner = new LocalRunner(config);
    const metaReactor = new LocalReactor(metaRunner);

    // Bootstrap meta database with demo data. In prod (skipLogin=true) iam is
    // unused inside bootstrap, so passing null is safe despite the type cast.
    const bootstrapDone = bootstrap(rootMetaDb, getDatabase, (devIam ?? null) as unknown as IAMInterface, !devMode)
      .then(() => setBootstrapped(true))
      .catch(e => console.error('[bootstrap] failed:', e));
    bootstrapDoneRef.current = bootstrapDone;

    if (import.meta.env.DEV) {
      const w = window as typeof window & {
        __META_DB__?: Database;
        __GET_DB__?: typeof getDatabase;
        __PAGE__?: typeof pageRef;
      };
      w.__META_DB__ = rootMetaDb;
      w.__GET_DB__ = getDatabase;
      w.__PAGE__ = pageRef;
      // __IAM__ is assigned when iam is constructed (dev sync effect or prod IAM effect).
    }

    return { config, devMode, getDatabase, rootMetaDb, devIam, runner, reactor, llm, registry, metaIAM, metaRunner, metaReactor };
  }, []);

  // Dev mode: LocalIAM is built in useMemo (bootstrap needs it for auto-login);
  // sync it into state + ref on first mount so the render gate and llm closure
  // see it. One-shot via `iam ||` guard — never re-fires.
  useEffect(() => {
    if (iam || !devIam) return;
    iamRef.current = devIam;
    setIam(devIam);
    if (import.meta.env.DEV) {
      (window as typeof window & { __IAM__?: LocalIAM | FirebaseIAM }).__IAM__ = devIam;
    }
  }, [iam, devIam]);

  // Prod mode: construct FirebaseIAM once the hostname-matched project and its
  // parent meta DB resolve. Using match.config means each tenant authenticates
  // against its own Firebase Auth (so the hosting domain is authorized) and its
  // users live in parentMetaDb, not always mksys.
  useEffect(() => {
    if (iam || devMode || !match || !parentMetaDb) return;
    const matchConfig = (match.config as Config) ?? (mksysConfig as unknown as Config);
    const matchFC = new FirebaseConfig(
      matchConfig.projectId!,
      matchConfig.appId!,
      matchConfig.storageBucket!,
      matchConfig.apiKey!,
      matchConfig.authDomain!,
      matchConfig.messagingSenderId!,
      matchConfig.measurementId!,
    );
    const usersModel = { name: 'users', path: '/users', type: 'collection' as const, properties: [] as string[], actions: [] } as unknown as Model;
    const newIam = new FirebaseIAM(
      matchFC,
      (u: User) => setUser(u),
      () => setUser(null),
      async (firebaseUser) => {
        if (!firebaseUser) {
          setUser(null);
          setAuthChecked(true);
          return;
        }
        await bootstrapDoneRef.current;
        // Read through the ref: parentMetaDb may have changed since IAM
        // was constructed (e.g., project hierarchy re-resolved).
        const db = parentMetaDbRef.current;
        if (!db) return;
        const users = await db.list(usersModel, [], { email: firebaseUser.email });
        const current = iamRef.current;
        if (!current) return;
        if (users.length > 0) {
          const u = users[0] as User;
          if (!u.uid) await db.update(usersModel, u.id, { uid: firebaseUser.uid });
          current.login({ id: u.id, name: u.name, email: u.email, pic: u.pic ?? firebaseUser.photoURL ?? undefined, uid: firebaseUser.uid });
        } else {
          const newUser = await db.create(usersModel, {
            name: firebaseUser.displayName ?? firebaseUser.email ?? 'Unknown',
            email: firebaseUser.email ?? '',
            pic: firebaseUser.photoURL ?? '',
            uid: firebaseUser.uid,
          });
          current.login(newUser as User);
        }
        setAuthChecked(true);
      },
    );
    iamRef.current = newIam;
    setIam(newIam);
    if (import.meta.env.DEV) {
      (window as typeof window & { __IAM__?: LocalIAM | FirebaseIAM }).__IAM__ = newIam;
    }
  }, [iam, devMode, match, parentMetaDb]);

  // Subscribe to system models from root meta-database
  useEffect(() => {
    if (!bootstrapped) return;
    return rootMetaDb.list({...model, name: 'models', path: '/models'} as Model, [], undefined, undefined, undefined,
      (entities) => setModels(prev => { const next = entities as Model[]; const same = stringify(prev) === stringify(next); if (!same) console.log('[state] models:', next.length); return same ? prev : next; }));
  }, [rootMetaDb, bootstrapped]);

  // Subscribe to system properties from root meta-database
  useEffect(() => {
    if (!bootstrapped) return;
    return rootMetaDb.list({...model, name: 'properties', path: '/properties'} as Model, [], undefined, undefined, undefined,
      (entities) => setProperties(prev => { const next = entities as Property[]; const same = stringify(prev) === stringify(next); if (!same) console.log('[state] properties:', next.length); return same ? prev : next; }));
  }, [rootMetaDb, bootstrapped]);

  // Subscribe to system actions from root meta-database
  useEffect(() => {
    if (!bootstrapped) return;
    return rootMetaDb.list({...model, name: 'actions', path: '/actions'} as Model, [], undefined, undefined, undefined,
      (entities) => setActions(prev => { const next = entities as Action[]; const same = stringify(prev) === stringify(next); if (!same) console.log('[state] actions:', next.length); return same ? prev : next; }));
  }, [rootMetaDb, bootstrapped]);

  // Hydrate system models (from root)
  useEffect(() => {
    const next = hydrateModels(models, properties, actions);
    setHydratedModels(prev => { const same = stringify(prev) === stringify(next); if (!same) console.log('[state] hydratedModels:', next.length); return same ? prev : next; });
  }, [models, properties, actions]);

  // Discover all projects recursively across database hierarchy
  useEffect(() => {
    if (hydratedModels.length === 0) return;
    const projectModel = hydratedModels.find(m => m.path === '/projects');
    if (!projectModel) return;

    const hostname = window.location.hostname;

    const t0 = performance.now();
    console.log('[discover] start');

    // Fan out sibling DBs in parallel so cold Firestore handshakes overlap.
    // Parent references are resolved in a second pass once all DBs are listed.
    const discover = async (db: Database, visited: Set<string>, label: string): Promise<Project[]> => {
      const t = performance.now();
      const projects = await db.list(projectModel, ['config']) as Project[];
      console.log(`[discover] list /projects from ${label}: ${projects.length} (${Math.round(performance.now() - t)}ms)`);
      const fresh = projects.filter(p => {
        if (visited.has(p.id)) return false;
        visited.add(p.id);
        return true;
      });
      const childResults = await Promise.all(fresh.map(p => {
        const childDb = getDatabase(p.id, p.database, p.config);
        return childDb === db ? Promise.resolve([] as Project[]) : discover(childDb, visited, p.url || p.id);
      }));
      return [...fresh, ...childResults.flat()];
    };

    discover(rootMetaDb, new Set(), 'root').then(projects => {
      console.log(`[discover] done: ${projects.length} projects total (${Math.round(performance.now() - t0)}ms)`);
      console.log('[app] discovered projects:', projects.map(p => p.url), 'hostname:', hostname);
      const result = resolveProjectHierarchy(projects, hostname);
      console.log('[app] hierarchy result:', result ? { match: result.match?.url, selectable: result.selectableProjects.map(p => p.url) } : null);
      if (result) {
        setSelectableProjects(prev => { const same = stringify(prev) === stringify(result.selectableProjects); if (!same) console.log('[state] selectableProjects:', result.selectableProjects.length); return same ? prev : result.selectableProjects; });
        setMatch(result.match);
        // Authorizations for a project live in its parent's DB. resolveProjectHierarchy resolves
        // the parent for us; it's `match` in the children-case and the looked-up project in the leaf-case.
        const { parent } = result;
        console.log('[app] parent:', parent, 'selectable:', result.selectableProjects);
        const nextMetaDb = parent ? getDatabase(parent.id, parent.database, parent.config) : rootMetaDb;
        setParentMetaDb(prev => { if (prev === nextMetaDb) return prev; console.log('[state] parentMetaDb'); return nextMetaDb; });
      }
      // Signals "discovery is done" to the render-gate chain so we can
      // differentiate "loading" from "hostname not in tree".
      setDiscovered(true);
    });
  }, [rootMetaDb, hydratedModels, getDatabase]);

  // Subscribe to domain models + properties + actions from parentMetaDb and hydrate (when different from root)
  const [domainModels, setDomainModels] = useState<Model[]>([]);
  const [domainProperties, setDomainProperties] = useState<Property[]>([]);
  const [domainActions, setDomainActions] = useState<Action[]>([]);

  useEffect(() => {
    if (!parentMetaDb || parentMetaDb === rootMetaDb) {
      // When meta DB is root (mksys, ordering URLs), system models already cover everything
      setDomainModels([]);
      setDomainProperties([]);
      setDomainActions([]);
      return;
    }
    const unsubs: (() => void)[] = [];
    const mm = { ...model, name: 'models', path: '/models' } as Model;
    const pm = { ...model, name: 'properties', path: '/properties' } as Model;
    const am = { ...model, name: 'actions', path: '/actions' } as Model;
    const t0 = performance.now();
    let firstModels = true, firstProps = true, firstActions = true;
    console.log('[domain] subscribe start');
    unsubs.push(parentMetaDb.list(mm, [], undefined, undefined, undefined,
      (entities) => setDomainModels(prev => { const next = entities as Model[]; const same = stringify(prev) === stringify(next); if (!same) console.log(`[state] domainModels: ${next.length}${firstModels ? ` (${Math.round(performance.now() - t0)}ms first)` : ''}`); firstModels = false; return same ? prev : next; })));
    unsubs.push(parentMetaDb.list(pm, [], undefined, undefined, undefined,
      (entities) => setDomainProperties(prev => { const next = entities as Property[]; const same = stringify(prev) === stringify(next); if (!same) console.log(`[state] domainProperties: ${next.length}${firstProps ? ` (${Math.round(performance.now() - t0)}ms first)` : ''}`); firstProps = false; return same ? prev : next; })));
    unsubs.push(parentMetaDb.list(am, [], undefined, undefined, undefined,
      (entities) => setDomainActions(prev => { const next = entities as Action[]; const same = stringify(prev) === stringify(next); if (!same) console.log(`[state] domainActions: ${next.length}${firstActions ? ` (${Math.round(performance.now() - t0)}ms first)` : ''}`); firstActions = false; return same ? prev : next; })));
    return () => unsubs.forEach(u => u());
  }, [parentMetaDb, rootMetaDb]);

  // Hydrate domain models (mutates DB(2) store objects so population works)
  useEffect(() => {
    hydrateModels(domainModels, domainProperties, domainActions);
  }, [domainModels, domainProperties, domainActions]);

  // Load user's groups from parentMetaDb (using system models as query descriptors)
  useEffect(() => {
    if (!user || !parentMetaDb || hydratedModels.length === 0 || selectableProjects.length === 0) return;
    const groupModel = hydratedModels.find(m => m.path === '/groups') as Model;
    const t0 = performance.now();
    let first = true;
    console.log('[groups] subscribe start');
    return parentMetaDb.list(groupModel, [], { users: { $contains: user.id } }, undefined, undefined,
      (entities) => {
        if (entities.length === 0) setAuthResolved(true);
        setGroups(prev => { const next = entities as Group[]; const same = stringify(prev) === stringify(next); if (!same) console.log(`[state] groups: ${next.length}${first ? ` (${Math.round(performance.now() - t0)}ms first)` : ''}`, next.map(g => g.name)); first = false; return same ? prev : next; });
      });
  }, [parentMetaDb, hydratedModels, user, selectableProjects]);

  // Subscribe to authorizations from parentMetaDb (using system models as query descriptors)
  const hadGroups = useRef(false);
  useEffect(() => {
    if (groups.length > 0) hadGroups.current = true;
    if (!parentMetaDb || hydratedModels.length === 0 || groups.length === 0) {
      // Clear stale authn when groups become empty after having had groups (e.g., group membership revoked while logged in)
      if (groups.length === 0 && hadGroups.current) { setAuthn([]); hadGroups.current = false; }
      return;
    }

    const userGroupIds = groups.map(group => group.id);
    const authorizationModel = hydratedModels.find(m => m.path === '/authorizations') as Model;
    const matchedProjectIds = selectableProjects.map(p => p.id);
    const t0 = performance.now();
    let first = true;
    console.log('[authn] subscribe start');
    // Track last-seen authz via closure state so the change detection
    // doesn't need to run side-effects (setProject, console.log) from
    // inside the setAuthn updater — React may invoke an updater twice
    // under StrictMode, which would double-fire any work embedded there.
    let prevAuthzStr: string | undefined;
    return parentMetaDb.list(authorizationModel, ['group', 'capability', 'capability.model.**', 'project.**'], { group: { $in: userGroupIds }, project: { $in: matchedProjectIds } }, undefined, undefined, (entities) => {
      const authz = entities as Authorization[];
      const nextStr = stringify(authz);
      const same = prevAuthzStr === nextStr;
      prevAuthzStr = nextStr;
      setAuthResolved(true);
      setAuthn(prev => same ? prev : authz);
      if (!same) {
        console.log(`[state] authn: ${authz.length}${first ? ` (${Math.round(performance.now() - t0)}ms first)` : ''}`);
        setProject(p => p ?? (authz.length > 0 ? authz[0].project ?? null : null));
      }
      first = false;
    });
  }, [parentMetaDb, hydratedModels, groups, selectableProjects]);

  // Evaluate auth conditions and push to project database
  useEffect(() => {
    const db = project?.id ? getDatabase(project.id, project.database, project.config) : null;
    if (!db || !user || !project) {
      db?.setConditions({ read: {}, write: {} });
      return;
    }
    const read: Record<string, Record<string, unknown>> = {};
    const write: Record<string, Record<string, unknown>> = {};
    for (const auth of authn) {
      if ((auth.project as Project)?.id !== project.id) continue;
      const cap = auth.capability as Capability;
      const modelName = (cap?.model as Model)?.name;
      if (!modelName || !auth.condition) continue;
      let filter: Record<string, unknown>;
      try {
        const result = runner.evaluate(auth.condition, {});
        if (result && typeof result === 'object' && !Array.isArray(result)) {
          filter = result as Record<string, unknown>;
        } else {
          console.warn(`Auth condition for ${modelName} returned non-object, denying access:`, result);
          filter = { id: '' };
        }
      } catch {
        console.warn(`Auth condition for ${modelName} threw, denying access`);
        filter = { id: '' };
      }
      const action = cap.action as string;
      if (action === 'read' || action === 'all') read[modelName] = filter;
      if (action === 'write' || action === 'all') write[modelName] = filter;
    }
    // Skip setConditions if unchanged — prevents re-notifying all subscribers when project DB === meta DB
    // Also skip when empty and no prior conditions exist (nothing to set or clear)
    const conditionsJson = JSON.stringify({ read, write });
    if (conditionsJson === prevConditionsRef.current) return;
    const empty = Object.keys(read).length === 0 && Object.keys(write).length === 0;
    if (empty && !prevConditionsRef.current) { prevConditionsRef.current = conditionsJson; return; }
    prevConditionsRef.current = conditionsJson;
    console.log('[state] conditions:', Object.keys(read).length, 'read,', Object.keys(write).length, 'write');
    db.setConditions({ read, write });
  }, [authn, user, project, getDatabase, runner]);

  // Push models to databases for path-string resolution
  const effectiveMetaDb = parentMetaDb ?? rootMetaDb;
  useEffect(() => {
    const authnModels = authn.map(a => (a.capability as Capability)?.model as Model).filter(Boolean);
    const db = project?.id ? getDatabase(project.id, project.database, project.config) : null;
    db?.setModels(authnModels);
    effectiveMetaDb.setModels([...hydratedModels, ...domainModels]);
  }, [authn, project, getDatabase, hydratedModels, domainModels, effectiveMetaDb]);

  // Both use effectiveMetaDb as initial database. metaServices keeps it (model definitions live here).
  // services.database is overridden by setContext wrapper to the project's entity DB once a project is selected.
  // On first render iam is null; fall back to metaIAM as a placeholder for the type's sake —
  // the render gate below returns a spinner until the real iam lands, and the sync effect swaps it in.
  const services: Services = { iam: iam ?? metaIAM, runner, reactor, database: effectiveMetaDb, llm, registry };
  const metaServices: Services = { iam: metaIAM, runner: metaRunner, reactor: metaReactor, database: effectiveMetaDb, llm, registry };

  // Guard against empty or malformed project.color — MUI's decomposeColor throws
  // (error #9) on unrecognized formats, including '', and crashes the whole app.
  const rawColor = project?.color;
  const primaryColor = typeof rawColor === 'string' && /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(rawColor)
    ? rawColor
    : '#000000';
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: { main: primaryColor },
    },
  });

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--brand', primaryColor);
    root.setProperty('--brand-foreground', brandForeground(primaryColor));
    root.setProperty('--brand-tint', brandTintPercent(primaryColor));
  }, [primaryColor]);

  const [context, _setContext] = useState<ContextType>({
    ...services,
    meta: metaServices,
    project,
    user,
    config,
    authn,
    time: new Date(),
    page: pageRef
  });

  contextRef.current = context;
  runner.app = context as unknown as Record<string, unknown>;

  // Wrap setter to automatically derive database from project and sync meta
  const setContext = useCallback<typeof _setContext>((action) => {
    _setContext(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      const project = next.project;
      const db = project?.id ? getDatabase(project.id, project.database, project.config) : effectiveMetaDb;
      const meta = { ...next.meta, database: effectiveMetaDb };
      return { ...next, database: db, meta };
    });
  }, [getDatabase, effectiveMetaDb]);

  // Sync authn, user state into context when they change
  useEffect(() => {
    setContext(prev => ({ ...prev, authn, user }));
  }, [authn, user, setContext]);

  // Swap the placeholder metaIAM in context for the real iam once it's built.
  // Identity guard prevents a no-op write on subsequent renders.
  useEffect(() => {
    if (!iam) return;
    setContext(prev => prev.iam === iam ? prev : { ...prev, iam });
  }, [iam, setContext]);

  // Set initial project from auth callback (only when context.project is not yet set)
  useEffect(() => {
    if (project) {
      setContext(prev => prev.project ? prev : { ...prev, project });
    }
  }, [project, setContext]);

  // Sync project from context (sidebar switch) back to state for condition evaluation
  useEffect(() => {
    const contextProject = context.project as Project | null;
    if (contextProject && contextProject.id !== project?.id) {
      setProject(contextProject);
      context.page.current?.navigate?.('/');
    }
  }, [context.project, context.page, project?.id]);

  // Offline gate disabled — navigator.onLine false-positives (cold boot, wake-from-sleep, VPN) blanked the app. See plans/offline-detection.md.
  // const [online, setOnline] = useState(navigator.onLine);
  // useEffect(() => {
  //   const goOnline = () => setOnline(true);
  //   const goOffline = () => setOnline(false);
  //   window.addEventListener('online', goOnline);
  //   window.addEventListener('offline', goOffline);
  //   return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  // }, []);
  //
  // if (!online) {
  //   return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
  //     <Notice detail="You're offline. Don't worry — your progress is safe and the app will resume automatically once you're back online." />
  //   </div>;
  // }

  // Hostname isn't registered in the project tree — discover has run
  // and resolveProjectHierarchy returned null, so no parentMetaDb will
  // ever be set. Gate placed BEFORE the Login gate because signing in
  // doesn't help when nothing is configured to serve this host.
  if (discovered && !parentMetaDb) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Notice tone="unknown" detail={`This website isn't fully configured yet: no project is registered to serve it.`} />
    </div>;
  }

  // IAM hasn't been constructed yet — dev waits one tick for the mount
  // effect, prod waits for the match + parentMetaDb resolve. Spinner holds
  // the tree out of the way so the placeholder metaIAM in context is never
  // actually rendered against.
  if (!iam) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Notice />
    </div>;
  }

  // Show login page when not authenticated in production mode.
  // Gated on `discovered` AND `parentMetaDb` so we don't flash Login
  // while the hostname→project resolution is still pending — Firebase
  // Auth restores a cached session quickly (authChecked flips), but
  // discover is a network round-trip behind it. Without these extra
  // gates Login renders for one frame on every cold load.
  if (!user && bootstrapped && !devMode && iam instanceof FirebaseIAM && authChecked && discovered && parentMetaDb) {
    return (
      <StrictMode>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Login auth={iam.getAuth()} />
        </ThemeProvider>
      </StrictMode>
    );
  }

  // Auth chain still loading — show full-screen spinner
  if (authn.length === 0 && !authResolved) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Notice />
    </div>;
  }

  // Authenticated but no permissions — offer logout
  if (authn.length === 0 && user && parentMetaDb && authResolved && groups.length === 0) {
    return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Notice tone="unknown" detail={`${user.email} doesn't have access. Contact your administrator.`} />
      <button
        onClick={() => iam.logout()}
        style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '0.375rem', background: '#000', color: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}
      >
        Sign in with a different account
      </button>
    </div>;
  }

  // Gate on parentMetaDb: without it, async discover() hasn't resolved yet — not a true 404
  if (authn.length === 0 && user && parentMetaDb && authResolved) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <h1>404 — Not Found</h1>
    </div>;
  }

  return (
    <StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Context.Provider value={[context, setContext]}>
          <BrowserRouter>
            <Layout>
              <Routes>
                <Route path="/*" element={<Page />} />
              </Routes>
            </Layout>
          </BrowserRouter>
        </Context.Provider>
      </ThemeProvider>
    </StrictMode>
  )
}

export default App
